import {knownRideMessage, translateRide, type RideLanguage} from "./ride-language";

export const RIDE_TIME_ZONE = "Asia/Jerusalem";

export function whatsappHref(phone: string | undefined, message: string): string | undefined {
  const value = (phone || "").trim().replace(/^n/, "");
  // Only an international number supplied by the authorized ride API.
  if (!/^\+?[1-9][0-9 ()-]{6,20}$/.test(value)) return;
  const digits = value.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) return;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function rideDay(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {timeZone: RIDE_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit"}).formatToParts(date);
  return ["year", "month", "day"].map(type => parts.find(part => part.type === type)!.value).join("-");
}

export function nextRideDay(date = new Date()): string {
  const next = new Date(`${rideDay(date)}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

export function rideDateLabel(value: string, locale = "en-GB"): string {
  return new Date(`${value}T12:00:00`).toLocaleDateString(locale, {weekday: "short", day: "numeric", month: "short"});
}

export function apiStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return;
  const value = error as {status?: unknown; originalStatus?: number};
  return typeof value.status === "number" ? value.status : value.originalStatus;
}

export function apiMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  const status = apiStatus(error);
  if (status === 401) return "Your session has expired. Sign in to continue.";
  if (status === 403) return "You don’t have access to this group. Please check your invitation.";
  if (status === 429) return "Too many attempts. Please wait before trying again.";
  const data = (error as {data?: {error?: string}} | undefined)?.data;
  return typeof data?.error === "string" ? knownRideMessage(data.error, fallback) : fallback;
}

// Only return to screens in this group; never accept an arbitrary redirect URL.
export function inviteDestination(groupId: string, params: URLSearchParams): string {
  const base = `/quick-actions/${encodeURIComponent(groupId)}/rides`;
  if (params.get("next") === "add") return `${base}/add`;
  const query = new URLSearchParams();
  for (const key of ["ride", "date", "view", "page"]) {
    const value = params.get(key);
    if (value) query.set(key, value);
  }
  return `${base}${query.size ? `?${query}` : ""}`;
}

export function rideCalendar(ride: {rideId: string; rideDateTime: string; toDest: string}, pickup: string, language: RideLanguage = "en"): string {
  // Escape every untrusted text field: no injected iCalendar properties/events.
  const escape = (text: string) => text.replace(/\\/g, "\\\\").replace(/\r?\n|\r/g, "\\n").replace(/;/g, "\\;").replace(/,/g, "\\,");
  const stamp = (date: Date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  return ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Cherri//Group rides//EN", "BEGIN:VEVENT",
    `UID:${encodeURIComponent(ride.rideId)}@cherri.app`, `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(new Date(ride.rideDateTime))}`,
    `SUMMARY:${escape(translateRide("Cherri ride to {destination}", language, {destination: ride.toDest}))}`, `LOCATION:${escape(pickup)}`,
    `DESCRIPTION:${escape(translateRide("Confirm pickup timing with your driver. This is the ride departure time; later stops may differ.", language))}`,
    "END:VEVENT", "END:VCALENDAR", ""].join("\r\n");
}
